import "./job-question-list.component.scss";
import { useCallback, useContext, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faPlus, faChevronRight, faBriefcase, faShapes, faXmark } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import Lottie from "lottie-react";
import LOADING from "../../assets/json/loading.json";
import { ProviderContext } from "../../contexts/provider.context";
import tokenService from "../../services/token.service";
import { QuestionsSummaryModel } from "../../model/questions-summary.model";
import { getInterviewQuestion, getInterviewQuestionByUserID } from "../../services/api.service";
import PaginationComponent from "../../shared/pagination/pagination.component";

const JobQuestionListComponent = () => {
    const [isLoading, setIsLoading] = useState(true);
    const { setMenuActive, profileModel }: any = useContext(ProviderContext);
    const [interviewModel, setInterviewModel] = useState<QuestionsSummaryModel[]>([]);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalItems: 0,
        itemsPerPage: 10,
        hasNext: false,
        lastDocId: null,
    });
    const [error, setError] = useState<string | null>(null);
    const [isCreateTypeModalOpen, setIsCreateTypeModalOpen] = useState(false);
    const navigate = useNavigate();
    const companyId = tokenService.getHR9CompanyUUID() || "";

    const getInterviewList = useCallback(
        (page = 1) => {
            setIsLoading(true);
            getInterviewQuestion(page, pagination.itemsPerPage)
                .then((res) => {
                    if (res.data.is_success) {
                        setInterviewModel(res.data.data as QuestionsSummaryModel[]);
                        const p = res.data.pagination;
                        setPagination({
                            currentPage: p.page,
                            totalItems: p.total,
                            itemsPerPage: p.limit,
                            hasNext: p.has_next,
                            lastDocId: p.last_doc_id,
                        });
                    }
                    setIsLoading(false);
                })
                .catch((err) => {
                    setIsLoading(false);
                    setError("เกิดข้อผิดพลาดในการโหลดข้อมูล");
                    console.error(err);
                });
        },
        [pagination.itemsPerPage],
    );

    const getInterviewListByID = useCallback(
        (user_uuid: string, page = 1) => {
            setIsLoading(true);
            getInterviewQuestionByUserID(user_uuid, page, pagination.itemsPerPage)
                .then((res) => {
                    if (res.data.is_success) {
                        setInterviewModel(res.data.data as QuestionsSummaryModel[]);
                        const p = res.data.pagination;
                        setPagination({
                            currentPage: p.page,
                            totalItems: p.total,
                            itemsPerPage: p.limit,
                            hasNext: p.has_next,
                            lastDocId: p.last_doc_id,
                        });
                    }
                    setIsLoading(false);
                })
                .catch((err) => {
                    setIsLoading(false);
                    setError("เกิดข้อผิดพลาดในการโหลดข้อมูล");
                    console.error(err);
                });
        },
        [pagination.itemsPerPage],
    );

    useEffect(() => {
        if (!profileModel?.roleName) return;
        if (profileModel.roleName === "ROLE_SUPER_ADMIN") {
            getInterviewList();
        } else if (profileModel.uuid) {
            getInterviewListByID(profileModel.uuid);
        }
    }, [companyId, getInterviewList, getInterviewListByID, profileModel?.roleName, profileModel?.uuid]);

    useEffect(() => {
        setMenuActive(2);
    }, [setMenuActive]);

    const clickRow = (item: QuestionsSummaryModel) => {
        navigate("/" + tokenService.getHR9CompanyUUID() + "/job/" + item.job_question_id);
    };

    const formatDate = (dateString: string | Date): string => {
        if (!dateString) return "—";
        return new Date(dateString)
            .toLocaleString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
                timeZone: "Asia/Bangkok",
            })
            .replace(",", " /");
    };

    const goCreateInterviewQuestion = () => {
        if (tokenService.getHR9CompanyUUID()) {
            navigate("/" + tokenService.getHR9CompanyUUID() + "/create-job-questions");
        } else {
            navigate("/login");
        }
    };

    const goCreateGeneralQuestion = () => {
        if (tokenService.getHR9CompanyUUID()) {
            navigate("/" + tokenService.getHR9CompanyUUID() + "/create-general-questions");
        } else {
            navigate("/login");
        }
    };

    const handlePageChange = (page: number) => {
        if (!profileModel?.roleName) return;
        if (profileModel.roleName === "ROLE_SUPER_ADMIN") {
            getInterviewList(page);
        } else if (profileModel.uuid) {
            getInterviewListByID(profileModel.uuid, page);
        }
    };

    return (
        <div className="jql-page">
            {/* ── Page header ── */}
            <div className="jql-page-header">
                <div>
                    <h1 className="jql-page-title">ชุดคำถามสัมภาษณ์</h1>
                    <p className="jql-page-sub">
                        {pagination.totalItems > 0 ? `${pagination.totalItems} ชุดคำถามทั้งหมด` : "ยังไม่มีชุดคำถาม"}
                    </p>
                </div>
                {profileModel?.roleName === "ROLE_SUPER_ADMIN" && (
                    <button className="jql-add-btn" onClick={() => setIsCreateTypeModalOpen(true)}>
                        <FontAwesomeIcon icon={faPlus} />
                        เพิ่มชุดคำถาม
                    </button>
                )}
            </div>

            {/* ── Table card ── */}
            <div className="jql-table-card">
                {isLoading ? (
                    <div className="jql-state-box">
                        <Lottie animationData={LOADING} className="jql-loading-anim" />
                        <span className="jql-state-text">กำลังโหลด...</span>
                    </div>
                ) : interviewModel.length === 0 ? (
                    <div className="jql-state-box">
                        <div className="jql-empty-icon">
                            <FontAwesomeIcon icon={faSearch} />
                        </div>
                        <span className="jql-state-text">ไม่พบข้อมูลชุดคำถาม</span>
                        {profileModel?.roleName === "ROLE_SUPER_ADMIN" && (
                            <button className="jql-add-btn" style={{ marginTop: 12 }} onClick={() => setIsCreateTypeModalOpen(true)}>
                                <FontAwesomeIcon icon={faPlus} />
                                เพิ่มชุดคำถาม
                            </button>
                        )}
                    </div>
                ) : (
                    <table className="jql-table">
                        <thead>
                            <tr>
                                <th className="jql-th jql-col-name">ตำแหน่งงาน</th>
                                <th className="jql-th jql-col-type">ประเภท</th>
                                <th className="jql-th jql-col-date">วันที่สร้าง</th>
                                <th className="jql-th jql-col-creator">ผู้สร้าง</th>
                                <th className="jql-th jql-col-stat">Complete / เชิญ</th>
                                <th className="jql-th jql-col-action"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {interviewModel.map((item, idx) => {
                                const completed = item.completed_reports_count ?? 0;
                                const total = item.candidates_count ?? 0;
                                const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
                                return (
                                    <tr key={idx} className="jql-tr" onClick={() => clickRow(item)}>
                                        <td className="jql-td jql-col-name">
                                            <span className="jql-name-main">{item.job_position.name}</span>
                                        </td>
                                        <td className="jql-td jql-col-type">
                                            <span className="jql-type-chip">{item.job_position.job_title}</span>
                                        </td>
                                        <td className="jql-td jql-col-date jql-text-muted">
                                            {formatDate(item.created_at)}
                                        </td>
                                        <td className="jql-td jql-col-creator jql-text-muted">
                                            {item.created_by || "—"}
                                        </td>
                                        <td className="jql-td jql-col-stat">
                                            <div className="jql-stat-wrap">
                                                <span className="jql-stat-nums">
                                                    <strong>{completed}</strong>
                                                    <span className="jql-stat-slash">/{total}</span>
                                                </span>
                                                <div className="jql-stat-bar">
                                                    <div
                                                        className="jql-stat-bar-fill"
                                                        style={{ width: `${pct}%` }}
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                        <td className="jql-td jql-col-action">
                                            <span className="jql-arrow">
                                                <FontAwesomeIcon icon={faChevronRight} />
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* ── Pagination ── */}
            {!isLoading && interviewModel.length > 0 && (
                <div className="jql-pagination-wrap">
                    <PaginationComponent
                        currentPage={pagination.currentPage}
                        totalItems={pagination.totalItems}
                        itemsPerPage={pagination.itemsPerPage}
                        hasNext={pagination.hasNext}
                        onPageChange={handlePageChange}
                        isLoading={false}
                    />
                </div>
            )}

            {error && <div className="jql-error">{error}</div>}

            {isCreateTypeModalOpen && (
                <div
                    className="jql-create-type-modal-overlay"
                    onClick={() => setIsCreateTypeModalOpen(false)}
                >
                    <div className="jql-create-type-modal" onClick={(e) => e.stopPropagation()}>
                        <button
                            className="jql-create-type-close"
                            onClick={() => setIsCreateTypeModalOpen(false)}
                            aria-label="close"
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </button>
                        <h3>เลือกประเภทคำถาม</h3>
                        <p>ต้องการสร้างคำถามแบบไหน</p>

                        <button
                            className="jql-create-type-option"
                            onClick={() => {
                                setIsCreateTypeModalOpen(false);
                                goCreateInterviewQuestion();
                            }}
                        >
                            <span className="icon"><FontAwesomeIcon icon={faBriefcase} /></span>
                            <span>
                                <strong>คำถามสัมภาษณ์งาน</strong>
                                <small>ไปยัง flow เดิมสำหรับสร้างชุดคำถามงาน</small>
                            </span>
                        </button>

                        <button
                            className="jql-create-type-option"
                            onClick={() => {
                                setIsCreateTypeModalOpen(false);
                                goCreateGeneralQuestion();
                            }}
                        >
                            <span className="icon"><FontAwesomeIcon icon={faShapes} /></span>
                            <span>
                                <strong>คำถามทั่วไป</strong>
                                <small>ไปยังหน้าใหม่สำหรับ workflow คำถามทั่วไป</small>
                            </span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default JobQuestionListComponent;
